import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { translations, rtlLanguages } from './translations';

const LanguageContext = createContext(null);

const getByPath = (obj, path) => {
  return path.split('.').reduce((acc, key) => {
    if (acc && Object.prototype.hasOwnProperty.call(acc, key)) {
      return acc[key];
    }
    return undefined;
  }, obj);
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    if (typeof window === 'undefined') return 'en';
    const saved = window.localStorage.getItem('language') || 'en';
    return translations[saved] ? saved : 'en';
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem('language', language);

    const isRtl = rtlLanguages.has(language);
    document.documentElement.setAttribute('lang', language);
    document.documentElement.setAttribute('dir', isRtl ? 'rtl' : 'ltr');

    const title = getByPath(translations[language], 'meta.title') || translations.en.meta.title;
    const description =
      getByPath(translations[language], 'meta.description') || translations.en.meta.description;
    document.title = title;

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', description);
    }
  }, [language]);

  const t = useMemo(() => {
    return (path) => {
      const value = getByPath(translations[language], path);
      if (value !== undefined) return value;
      const fallback = getByPath(translations.en, path);
      return fallback !== undefined ? fallback : path;
    };
  }, [language]);

  const languageOptions = useMemo(() => {
    return Object.keys(translations).map((code) => ({
      code,
      label: translations[code].languageLabel || code,
    }));
  }, []);

  return (
    <LanguageContext.Provider
      value={{
        language,
        setLanguage,
        t,
        languageOptions,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return ctx;
};
